"use client";

import { SubscriptionStatus } from "@prisma/client";
import React from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowRight from "@/app/(components)/generics/Icons/IconArrowRight";
import IconZap from "@/app/(components)/generics/Icons/IconZap";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { BILLING_METERS } from "@/app/(components)/specifics/billing/Billing/Billing.logic";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useFetchSubscription } from "@/app/hooks/fetchs/credits/useFetchSubscription";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UPlan } from "@/app/utils/UPlan";
import { UWallet } from "@/app/utils/UWallet";
import { Limits } from "@/app/vars/Limits";
import sidebarStyles from "../../Sidebar.module.css";
import { PLAN_STATUS_LABEL } from "./SidebarPlan.logic";
import styles from "./SidebarPlan.module.css";

type Props = DefaultProps & {
  isOpen: boolean;
};

export default function SidebarPlan(props: Props) {
  const T = useT();
  const { params, isOpen } = props;

  const user = useAuthUser();
  const { data: overview } = useFetchSubscription({ enabled: !!user });

  const stored = overview?.subscription;
  const hasPlan = !!UPlan.activeSubscription(stored);
  const status = hasPlan || stored?.status === SubscriptionStatus.PAST_DUE ? stored?.status : null;
  const isFreeTrial = !!overview && !stored;
  const plan = overview?.plan;

  const hours = user ? UWallet.spendableHours(user, hasPlan) : 0;
  const prospects = user ? UWallet.spendableProspects(user, hasPlan) : 0;

  const planName = !overview
    ? ""
    : status
      ? T(PLAN_STATUS_LABEL[status])
      : T(stored ? TKey.PLAN_NAME_NONE : TKey.PLAN_NAME_TRIAL);
  const manageLabel = hasPlan ? T(TKey.PLAN_MANAGE) : T(TKey.PLAN_SEE_PLANS);

  const summary = T(TKey.CREDITS_WALLET_TOOLTIP)
    .replace("{{hours}}", String(hours))
    .replace("{{prospects}}", String(prospects));

  const openModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    ModalStore.getState().openBilling(params);
  };

  if (!isOpen) {
    return (
      <Tooltip
        position={TooltipPosition.RIGHT}
        gap={Limits.SIDEBAR_TOOLTIP_GAP}
        type={TooltipType.GREEN}
        content={summary}
        className={sidebarStyles.fullWidth}
        trigger={
          <div className={styles.collapsed}>
            <Button
              min={false}
              params={params}
              isLink={false}
              type={ButtonType.GHOST}
              iconOnly={true}
              full={false}
              beforeIcon={<IconZap />}
              className={sidebarStyles.navButton}
              ariaLabel={summary}
              onClick={openModal}
            />
          </div>
        }
      />
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.left}>
          <h3 className={styles.title}>{T(TKey.PLAN_LABEL)}</h3>
          <div className={styles.amountRow}>
            <IconZap size={16} className={styles.icon} />
            <strong className={styles.amount}>{planName}</strong>
          </div>
        </div>
        <button
          type="button"
          className={styles.actionBtn}
          aria-label={manageLabel}
          onClick={openModal}
        >
          <IconArrowRight size={14} />
        </button>
      </div>

      {(hasPlan || isFreeTrial) && user && plan && (
        <div className={styles.meters}>
          {BILLING_METERS.map((meter) => {
            const used = meter.used(user, plan, hasPlan);
            const total = Math.round(used + meter.left(user, hasPlan));

            return (
              <div className={styles.meter} key={meter.labelKey}>
                <div className={styles.meterHead}>
                  <span className={styles.meterLabel}>{T(meter.labelKey)}</span>
                  <span className={styles.meterValue}>
                    {used} / {total}
                  </span>
                </div>
                <span className={styles.track}>
                  <span
                    className={styles.fill}
                    style={{ width: `${total > 0 ? Math.min(100, (used / total) * 100) : 0}%` }}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
